import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

import Button from '../../components/ui/Button';
import AppIcon from '../../components/AppIcon';
import ProfileDataViewer from './components/ProfileDataViewer';
import RealtimeUpdateLogs from './components/RealtimeUpdateLogs';
import FieldSaveStatus from './components/FieldSaveStatus';
import RLSPolicyEvaluator from './components/RLSPolicyEvaluator';
import AuthContextInspector from './components/AuthContextInspector';

const AdminDebugConsole = () => {
  const { user, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [debugSession, setDebugSession] = useState({
    startTime: new Date(),
    events: []
  });

  useEffect(() => {
    if (user?.id) {
      logDebugEvent('Console opened', 'info');
    }
  }, [user]);

  const logDebugEvent = (message, type = 'info', data = null) => {
    const event = {
      id: Date.now(),
      timestamp: new Date(),
      message,
      type,
      data
    };
    setDebugSession(prev => ({
      ...prev,
      events: [event, ...prev?.events]?.slice(0, 100) // Keep last 100 events
    }));
  };

  const handleRefresh = () => {
    logDebugEvent('Manual refresh triggered', 'action');
    setRefreshTrigger(prev => prev + 1);
  };

  const handleExportDebugReport = () => {
    const report = {
      session: {
        startTime: debugSession?.startTime,
        duration: new Date() - debugSession?.startTime,
        eventCount: debugSession?.events?.length
      },
      user: {
        id: user?.id,
        email: user?.email
      },
      profile: userProfile,
      events: debugSession?.events
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-report-${user?.id}-${Date.now()}.json`;
    a?.click();
    URL.revokeObjectURL(url);

    logDebugEvent('Debug report exported', 'action');
  };

  const handleClearLogs = () => {
    setDebugSession({
      startTime: new Date(),
      events: []
    });
    logDebugEvent('Logs cleared', 'action');
  };

  const tabs = [
    { id: 'profile', label: 'Profile Data', icon: 'Database' },
    { id: 'logs', label: 'Update Logs', icon: 'FileText' },
    { id: 'fields', label: 'Field Status', icon: 'CheckSquare' },
    { id: 'rls', label: 'RLS Policies', icon: 'Shield' },
    { id: 'context', label: 'AuthContext', icon: 'Code' }
  ];

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 text-center">
          <AppIcon name="AlertTriangle" className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Authentication Required</h2>
          <p className="text-gray-400">Please log in to access the debug console.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AppIcon name="Terminal" className="w-8 h-8 text-green-500" />
              <div>
                <h1 className="text-2xl font-bold text-white">Admin Debug Console</h1>
                <p className="text-sm text-gray-400">Supabase user_profiles Troubleshooting</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <AppIcon name="RefreshCw" className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportDebugReport}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <AppIcon name="Download" className="w-4 h-4 mr-2" />
                Export Report
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearLogs}
                className="border-red-600 text-red-400 hover:bg-red-900/20"
              >
                <AppIcon name="Trash2" className="w-4 h-4 mr-2" />
                Clear Logs
              </Button>
            </div>
          </div>

          {/* Session Info */}
          <div className="mt-4 flex items-center gap-6 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <AppIcon name="User" className="w-4 h-4" />
              <span>User: {user?.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <AppIcon name="Clock" className="w-4 h-4" />
              <span>Session: {Math.floor((new Date() - debugSession?.startTime) / 1000)}s</span>
            </div>
            <div className="flex items-center gap-2">
              <AppIcon name="Activity" className="w-4 h-4" />
              <span>Events: {debugSession?.events?.length}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Tab Navigation */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
                  activeTab === tab?.id
                    ? 'bg-gray-900 text-green-500 border-b-2 border-green-500' :'text-gray-400 hover:text-gray-300 hover:bg-gray-700/50'
                }`}
              >
                <AppIcon name={tab?.icon} className="w-4 h-4" />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'profile' && (
          <ProfileDataViewer
            user={user}
            userProfile={userProfile}
            refreshTrigger={refreshTrigger}
            onEvent={logDebugEvent}
          />
        )}
        {activeTab === 'logs' && (
          <RealtimeUpdateLogs
            user={user}
            refreshTrigger={refreshTrigger}
            onEvent={logDebugEvent}
          />
        )}
        {activeTab === 'fields' && (
          <FieldSaveStatus
            user={user}
            userProfile={userProfile}
            refreshTrigger={refreshTrigger}
            onEvent={logDebugEvent}
          />
        )}
        {activeTab === 'rls' && (
          <RLSPolicyEvaluator
            user={user}
            refreshTrigger={refreshTrigger}
            onEvent={logDebugEvent}
          />
        )}
        {activeTab === 'context' && (
          <AuthContextInspector
            user={user}
            userProfile={userProfile}
            onEvent={logDebugEvent}
          />
        )}
      </div>
    </div>
  );
};

export default AdminDebugConsole;